<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);

const filters = defineModel({
  type: Array,
  default: [],
});

const getFilterKey = filter =>
  filter?.attributeKey || filter?.attribute_key || '';

const isKanbanFunnelFilter = filter => getFilterKey(filter) === 'kanban_funnel_id';
const isKanbanStageFilter = filter => getFilterKey(filter) === 'kanban_stage_id';

const selectedKanbanFunnelId = computed(() => {
  const funnelFilter = filters.value.find(isKanbanFunnelFilter);

  if (!funnelFilter) return null;

  const firstValue = Array.isArray(funnelFilter.values)
    ? funnelFilter.values[0]
    : funnelFilter.values;
  const rawFunnelId = firstValue?.id ?? firstValue;
  const parsedFunnelId = Number(rawFunnelId);
  return Number.isInteger(parsedFunnelId) && parsedFunnelId > 0
    ? parsedFunnelId
    : null;
});

const { filterTypes } = useConversationFilterContext(selectedKanbanFunnelId);
const folderNameLocal = ref(props.folderName);

watch(
  () => selectedKanbanFunnelId.value,
  (newFunnelId, previousFunnelId) => {
    if (!newFunnelId) return;

    const nextFilters = [...filters.value];
    const funnelFilterIndex = nextFilters.findIndex(isKanbanFunnelFilter);
    const stageFilterIndex = nextFilters.findIndex(isKanbanStageFilter);

    if (stageFilterIndex === -1 && funnelFilterIndex !== -1) {
      const funnelFilter = nextFilters[funnelFilterIndex];
      if (!funnelFilter?.queryOperator) {
        nextFilters[funnelFilterIndex] = {
          ...funnelFilter,
          queryOperator: 'and',
        };
      }

      nextFilters.splice(funnelFilterIndex + 1, 0, {
        attributeKey: 'kanban_stage_id',
        filterOperator: 'equal_to',
        values: {},
        queryOperator: 'and',
      });
      filters.value = nextFilters;
      return;
    }

    if (stageFilterIndex !== -1 && previousFunnelId !== newFunnelId) {
      const stageFilter = nextFilters[stageFilterIndex];
      const stageValues = stageFilter?.values;
      const hasSelectedStage = Boolean(
        Array.isArray(stageValues)
          ? stageValues.length
          : stageValues?.id || stageValues?.value || stageValues
      );
      if (hasSelectedStage) {
        nextFilters[stageFilterIndex] = {
          ...stageFilter,
          values: {},
        };
        filters.value = nextFilters;
      }
    }
  }
);

watch(
  () => filters.value,
  currentFilters => {
    const stageFilterIndex = currentFilters.findIndex(isKanbanStageFilter);
    if (stageFilterIndex === -1) return;

    const stageFilter = currentFilters[stageFilterIndex];
    if (!Array.isArray(stageFilter?.values)) return;

    const normalizedValue = stageFilter.values[0] || {};
    const nextFilters = [...currentFilters];
    nextFilters[stageFilterIndex] = {
      ...stageFilter,
      values: normalizedValue,
    };
    filters.value = nextFilters;
  },
  { deep: true }
);

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isFolderView">
      <div class="border-b border-n-weak pb-6">
        <Input
          v-model="folderNameLocal"
          :label="t('FILTER.FOLDER_LABEL')"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </div>
    </div>
    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="filter.id">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button
          v-if="isFolderView"
          sm
          solid
          blue
          :disabled="!folderNameLocal"
          @click="updateSavedCustomViews"
        >
          {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
